<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from 'vue';
import AppMenuModulo from './AppMenuModulo.vue';
import AppMenuItemLinha from './AppMenuItemLinha.vue';
import {getMenuModulos} from './menu.config';
import {filtrarFolhasPorTermo, obterTodasAsFolhas} from './menu.util';
import {useMenuPreferencias} from '@/layout/composables/useMenuPreferencias';
import type {FolhaComContexto, MenuItemFolha, MenuModulo as TMenuModulo} from './menu.type';

const modulos = computed<TMenuModulo[]>(() => getMenuModulos());

const modulosAccordion = computed(() => modulos.value.filter((modulo) => !modulo.flat));

const itemHome = computed<MenuItemFolha | null>(() => {
    const inicio = modulos.value.find((modulo) => modulo.id === 'inicio');
    return inicio?.subgrupos[0]?.itens[0] ?? null;
});

const todasAsFolhas = computed<FolhaComContexto[]>(() => obterTodasAsFolhas(modulos.value));

const {favoritos} = useMenuPreferencias();

const favoritosVisiveis = computed(() =>
    todasAsFolhas.value.filter((folha) => folha.item.id !== 'home' && favoritos.value.includes(folha.item.id))
);

const busca = ref('');
const termoBusca = computed(() => busca.value.trim());

const resultadosBusca = computed<FolhaComContexto[]>(() => filtrarFolhasPorTermo(todasAsFolhas.value, termoBusca.value));

const inputBuscaRef = ref<HTMLInputElement | null>(null);

const limparBusca = () => {
    busca.value = '';
    inputBuscaRef.value?.blur();
};

const aoPressionarTeclaGlobal = (evento: KeyboardEvent) => {
    const alvo = evento.target as HTMLElement | null;
    const emCampoDeTexto = !!alvo && /input|textarea/i.test(alvo.tagName);

    if (evento.key === '/' && !emCampoDeTexto) {
        evento.preventDefault();
        inputBuscaRef.value?.focus();
    }
};

onMounted(() => {
    document.addEventListener('keydown', aoPressionarTeclaGlobal);
});

onUnmounted(() => {
    document.removeEventListener('keydown', aoPressionarTeclaGlobal);
});
</script>

<template>
    <div class="menu-lateral">
        <div class="menu-busca">
            <i class="pi pi-search menu-busca__icone" aria-hidden="true"></i>
            <input
                ref="inputBuscaRef"
                v-model="busca"
                type="search"
                class="menu-busca__input"
                placeholder="Buscar tela…"
                aria-label="Buscar tela"
                autocomplete="off"
                @keydown.esc="limparBusca"
            />
        </div>

        <nav class="menu-lista" aria-label="Telas">
            <template v-if="termoBusca">
                <div class="menu-secao-label">
                    {{ resultadosBusca.length }} {{ resultadosBusca.length === 1 ? 'tela encontrada' : 'telas encontradas' }}
                </div>
                <app-menu-item-linha
                    v-for="folha in resultadosBusca"
                    :key="folha.item.id"
                    :item="folha.item"
                    :contexto="folha.modulo.label"
                ></app-menu-item-linha>
                <div v-if="!resultadosBusca.length" class="menu-vazio">
                    <strong>Nenhuma tela encontrada</strong>
                    <span>Tente outro termo de busca.</span>
                </div>
            </template>
            <template v-else>
                <app-menu-item-linha v-if="itemHome" :item="itemHome"></app-menu-item-linha>
                <template v-if="favoritosVisiveis.length">
                    <div class="menu-secao-label">Favoritos</div>
                    <app-menu-item-linha
                        v-for="folha in favoritosVisiveis"
                        :key="'favorito-' + folha.item.id"
                        :item="folha.item"
                    ></app-menu-item-linha>
                </template>
                <div class="menu-secao-label">Módulos</div>
                <app-menu-modulo v-for="modulo in modulosAccordion" :key="modulo.id" :modulo="modulo"></app-menu-modulo>
            </template>
        </nav>
    </div>
</template>

<style lang="scss" scoped></style>
